import {useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import moment from 'moment'
import React, {useMemo, useRef} from 'react'
import {SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {checkEmptyString} from '../../../utils/CommonUtils'
import {useAuth} from '../../authentication/AuthProvider'
import {severityNotification} from '../../notification/NotificationConstant'
import {showNotification} from '../../notification/NotificationService'
import {usePatientBasicCRUD} from '../../patient/query-keys/PatientKeysManager'
import {genericLogQueryType} from '../generic-log/GenericLogConstansts'
import {titleList} from '../patient/Constants'
import {basicDetailInformationList} from './StyledDetailInformationConstants'
import StyledDetailInformationSection from './StyledDetailInformationSection'
import {usePatientContext} from './StyleDetailInformationWrapper'

const StyledBasicDetailInformationSection = () => {
    useSignals()
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentViewedUser, handleChangeCurrentViewedUser} =
        usePatientContext()

    const {mutateAsync: createOrUpdatePatient} = usePatientBasicCRUD(
        QueryCRUDTypes.update,
        user?.id,
    )

    const {patientKeys} = QueryKeyManager(QueryKeyTypes.patient, user?.id)

    const patientQueryKeys = useMemo(() => {
        return patientKeys.detail({id: currentViewedUser?.id})
    }, [patientKeys, currentViewedUser?.id])

    const sectionRef = useRef()
    const queryClient = useQueryClient()
    const handleOnSubmit = async data => {
        if (
            userRole === SystemRole.ADMIN &&
            (!data.hospitalName || typeof data.hospitalId !== 'number')
        ) {
            showNotification(
                severityNotification.error,
                'Hospital is required !!',
            )
            return
        }

        if (
            !data?.firstName ||
            data?.firstName?.length < 0 ||
            !!checkEmptyString(data?.firstName)
        ) {
            showNotification(
                severityNotification.error,
                'First Name is required !!',
            )
            return
        }

        if (data?.dob) {
            const dob = moment(data.dob, 'DD-MM-YYYY')

            if (!dob.isValid()) {
                return showNotification(
                    severityNotification.error,
                    'Dob is invalid !!',
                )
            }

            const now = moment()
            if (!dob.isBefore(now)) {
                return showNotification(
                    severityNotification.error,
                    `Dob cannot be in the future.`,
                )
            }
        }

        let toPostData = {
            ...currentViewedUser,
            id: data?.id || null,
            title: data?.title || titleList[0].value,
            firstName: data?.firstName,
            lastName: data?.lastName,
            email: data?.email,
            gender: data?.gender,
            dob: data?.dob,
            nhsNumber: data?.nhsNumber,
        }

        const patientData = await createOrUpdatePatient(toPostData)
        if (patientData?.id) {
            handleChangeCurrentViewedUser(patientData)
            // queryClient.invalidateQueries(patientQueryKeys)

            const {genericLogKeys} = QueryKeyManager(
                QueryKeyTypes.genericLog,
                user?.id,
            )
            queryClient.invalidateQueries(
                genericLogKeys.pattern({
                    type: genericLogQueryType.userLog,
                }),
            )
            sectionRef.current.onSuccess()
        }
    }

    return (
        <React.Fragment>
            <div className="w-full">
                <StyledDetailInformationSection
                    ref={sectionRef}
                    data={currentViewedUser}
                    dataList={basicDetailInformationList}
                    onSubmit={handleOnSubmit}
                />
            </div>
        </React.Fragment>
    )
}

export default StyledBasicDetailInformationSection
